import { useGetChatParticipants, useGetThreads } from "../../services/queries"
import { useEffect, useState, useRef, useCallback, useMemo } from "react"
import {
  Avatar,
  Box,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  useMediaQuery,
  useTheme,
  Typography,
} from "@material-ui/core"
import NCText from "../../shared/NCText"
import NCInput from "../../shared/NCInput"
import NCButton from "../../shared/NCButton"
import NCModal from "../../shared/NCModal"
import NCAutocomplete from "../../shared/NCAutocomplete"
import FormField from "../../components/FormField"
import { Form, FormikProvider, useFormik } from "formik"
import * as Yup from "yup"
import { SearchIcon, AddIcon } from "../../libs/icons"
import NCSvgIcon from "../../shared/NCSvgIcon"
import NCLoader from "../../shared/NCLoader"
import { STORAGE } from "../../libs/storage"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import ChatProvider from "./ChatProvider"
import { useTranslation } from "react-i18next"
import { useCreateThread } from "../../services/mutations"
import { useQueryClient } from "react-query"
import { usePubNub } from "pubnub-react"
import { toast } from "react-toastify"
import moment from "moment"
import { USER_TYPE } from "../../libs/config"

const useStyles = makeStyles(theme => ({
  messageSection: {
    background: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  search: {
    width: "98%"
  },
  userListContainer: {
    marginTop: "0px",
    color: "rgba(16, 101, 142, 1)",  },
  leftPanel: {
    borderRight: "1px solid #E5E7EB",
    minWidth: 0,
    overflow: "hidden",
  },
  userItem: {
    borderBottom: "1px solid rgba(217, 217, 217, 1)",
    cursor: "pointer",
    paddingTop: "14px",
    paddingBottom: "14px",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: "rgba(30, 170, 196, 0.05)",
    },
  },
  h100: {
    height: "calc(100% - 30px)"
  },
  chatPanels: {
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    overflow: "hidden",
    flex: 1,
    display: "flex",
  },
  listContainer: {
    height: "calc(100vh - 142px)",
    overflowY: "auto",
    [theme.breakpoints.down("xs")]: {
      height: `calc(100vh - (${theme.mixins.toolbar.minHeight}px + 152px))`
    }
  },
  userText: {
    fontSize: "18px",
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    fontWeight: 700
  },
  selectedUserItem: {
    borderBottom: "1px solid rgba(217, 217, 217, 1)",
    cursor: "pointer",
    paddingTop: "14px",
    paddingBottom: "14px",
    backgroundColor: "rgba(209, 252, 245, 1)"
  },
  count: {
    minWidth: "18px",
    height: "18px",
    padding: "0 4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "20px",
    backgroundColor: "rgba(30, 170, 196, 1)",
    color: "white",
    fontSize: "11px",
    fontWeight: 700
  }
}))

const UserChannelList = () => {
  const cls = useStyles()

  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const createThread = useCreateThread()
  const pubnub = usePubNub()

  const [searchText, setSearchText] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceRef = useRef(null)
  const [debouncedParticipantSearch, setDebouncedParticipantSearch] = useState("")
  const participantDebounceRef = useRef(null)
  const { data: userList, isLoading: isUsersLoading } = useGetChatParticipants(debouncedParticipantSearch)
  const { data: threadsData, isLoading: isThreadsLoading } = useGetThreads(debouncedSearch)

  const [threads, setThreads] = useState([])
  const [selectedThread, setSelectedThread] = useState(null)

  // Chat/Group modals
  const [newGroupModal, setNewGroupModal] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()

  const mobileTheme = useTheme()
  // eslint-disable-next-line no-unused-vars
  const isSmallScreen = useMediaQuery(mobileTheme.breakpoints.down("sm"))
  const chatPath = "/direct-message/chat"
  const userListPath = "/direct-message"

  const isLoading = isUsersLoading || isThreadsLoading

  // Debounced server-side search
  const handleSearch = useCallback((e) => {
    const value = e.target.value
    setSearchText(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value)
    }, 400)
  }, [])

  // Debounced server-side search for group participants
  const handleParticipantSearch = useCallback(value => {
    if (participantDebounceRef.current) clearTimeout(participantDebounceRef.current)
    participantDebounceRef.current = setTimeout(() => {
      setDebouncedParticipantSearch(value)
    }, 400)
  }, [])

  // Map threads to the format expected by renderChatItem
  useEffect(() => {
    if (!threadsData?.data?.data?.results) return

    const threadList = threadsData.data.data.results
    const currentUser = STORAGE.getUserDetails()
    const currentUserId = currentUser?.id

    const mapped = threadList.map(thread => {
      const isGroup = thread.thread_type === 2
      const otherParticipant = !isGroup
        ? thread.participants?.find(p => p.user !== currentUserId)
        : null

      return {
        id: thread.id,
        pubnub_channel_id: thread.pubnub_channel_id,
        name: isGroup ? thread.name : otherParticipant?.name || "Unknown",
        email: otherParticipant?.email || "",
        uuid: otherParticipant?.uuid || otherParticipant?.user || thread.id,
        avatar: otherParticipant?.avatar || "",
        user_type: otherParticipant?.user_type,
        count: thread.unread_count || 0,
        updated: thread.last_message?.created || thread.modified,
        lastMessage: thread.last_message?.text || (thread.last_message?.file_name ? "📎 File attachment" : ""),
        thread_type: thread.thread_type,
        participants: thread.participants,
      }
    })

    setThreads(mapped)
  }, [threadsData, userList])

  // Stable channel list derived from threads
  const channelsKey = useMemo(() => {
    return threads
      .map(t => t.pubnub_channel_id || String(t.id))
      .filter(Boolean)
      .sort()
      .join(",")
  }, [threads])

  // Stable listener callback — adds a delay so the server has time to persist
  const handlePubNubMessage = useCallback((msgEvent) => {
    // eslint-disable-next-line no-console
    console.log("[list] PubNub message received, refetching threads", msgEvent?.channel)
    queryClient.invalidateQueries({ queryKey: ["threads"] })
  }, [queryClient])

  // Track subscribed channels to avoid subscribe/unsubscribe loop
  const subscribedRef = useRef([])
  const pubnubRef = useRef(pubnub)
  pubnubRef.current = pubnub

  useEffect(() => {
    if (!channelsKey) return
    const channels = channelsKey.split(",")
    const prevKey = subscribedRef.current.join(",")

    // Only resubscribe if channels actually changed
    if (channelsKey === prevKey) return

    // eslint-disable-next-line no-console
    console.log("[list] subscribing to channels:", channels)

    // Unsubscribe old channels
    if (subscribedRef.current.length) {
      pubnubRef.current.unsubscribe({ channels: subscribedRef.current })
      pubnubRef.current.removeListener({ message: handlePubNubMessage })
    }

    const listener = { message: handlePubNubMessage }
    pubnubRef.current.addListener(listener)
    pubnubRef.current.subscribe({ channels })
    subscribedRef.current = channels

    return () => {
      // eslint-disable-next-line no-console
      console.log("[list] cleanup unsubscribing:", channels)
      pubnubRef.current.unsubscribe({ channels })
      pubnubRef.current.removeListener(listener)
    }
  }, [channelsKey, handlePubNubMessage])

  const handleSelectThread = thread => {
    // Immediately clear unread count in UI
    setThreads(prev => prev.map(t =>
      t.id === thread.id ? { ...t, count: 0 } : t
    ))
    setSelectedThread(thread)
    navigate(chatPath, {
      state: { thread },
      replace: location.pathname === chatPath,
    })
  }

  // Only drivers and aid monitors (filtered server-side via ?user_type=5,6)
  const userOptions = (userList?.data?.data?.results || []).map(u => ({
    id: u.id,
    name: `${u.name || u.email} (${USER_TYPE[u.user_type] || "User"})`
  }))

  const chatFormik = useFormik({
    initialValues: { participants: [], group_name: "" },
    validationSchema: Yup.object().shape({
      group_name: Yup.string().trim().required("Chat name is required"),
    }),
    onSubmit: (values, { resetForm }) => {
      const participants = Array.isArray(values.participants)
        ? values.participants
        : [values.participants]
      createThread.mutate(
        {
          thread_type: 2,
          participant_ids: participants.map(u => u?.id || u),
          name: values.group_name || undefined
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["threads"] })
            toast.success("Group created")
            setNewGroupModal(false)
            resetForm()
          },
          onError: () => toast.error("Failed")
        }
      )
    }
  })

  const groupModalContent = (
    <FormikProvider value={chatFormik}>
      <Form onSubmit={chatFormik.handleSubmit}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <FormField
              label="Chat Name"
              name="group_name"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <FormField
              as={NCAutocomplete}
              multiple
              options={userOptions}
              label={t("common.select_participants")}
              name="participants"
              onChange={v => chatFormik.setFieldValue("participants", v)}
              onSearch={handleParticipantSearch}
              required={true}
            />
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  )

  const renderChatItem = (props) => {
    const timeAgo = props.updated ? moment(props.updated).fromNow() : ""
    const rawMessage = props.lastMessage || "No messages yet"
    const lastMessage =
      rawMessage.length > 40 ? `${rawMessage.slice(0, 40)}…` : rawMessage
    return (
      <List className={cls.userListContainer} disablePadding key={props.id}>
        <ListItem
          className={
            selectedThread?.id !== props.id ? cls.userItem : cls.selectedUserItem
          }
          onClick={() => handleSelectThread(props)}
          button
        >
          <ListItemAvatar>
            <Avatar src={props.avatar} />
          </ListItemAvatar>

          <ListItemText
            style={{ minWidth: 0 }}
            primary={
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography component="span" className={cls.userText} noWrap style={{ flex: 1, minWidth: 0 }}>
                  {props.name}
                </Typography>
                <Typography component="span" style={{ fontSize: 12, color: "#999", flexShrink: 0, marginLeft: 8 }}>
                  {timeAgo}
                </Typography>
              </Box>
            }
            secondary={
              <Typography component="span" style={{ display: "block", fontSize: 14, color: "#777", maxWidth: "100%", overflow: "hidden" }} noWrap>
                {lastMessage}
              </Typography>
            }
          />

          {props.count > 0 && (
            <ListItemSecondaryAction>
              <span className={cls.count}>{props.count}</span>
            </ListItemSecondaryAction>
          )}
        </ListItem>
      </List>
    )
  }

  return (
    <Grid container className={cls.h100}>
      <Grid item xs={12}>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" mb={2} width="100%" flexWrap="wrap" gridGap={8}>
          <NCText variant={"h4"}>{t("chat.heading")}</NCText>
          <Box display="flex" flexDirection="row" alignItems="center" gridGap={8}>
            <NCInput
              value={searchText}
              startIcon={SearchIcon}
              onChange={handleSearch}
              className={["rounded"].join(" ")}
              placeholder={t("common.search_here")}
              style={{ width: 280 }}
            />
            <NCButton
              variant="contained"
              color="primary"
              size="small"
              startIcon={
                <NCSvgIcon component={AddIcon} />
              }
              onClick={() => setNewGroupModal(true)}
              style={{ whiteSpace: "nowrap", width: "auto", padding: "4px 14px" }}
            >
              Create Chat
            </NCButton>
          </Box>
        </Box>
      </Grid>
      <Box className={cls.chatPanels} flex={1}>
        <Grid container style={{ height: "100%", flex: 1, minWidth: 0 }}>
        {(isSmallScreen ? location.pathname === userListPath : true) && (
          <Grid item xs={12} md={4} className={cls.leftPanel}>
            <Grid container>
              <Grid item xs={12} className={cls.listContainer}>
                {threads.map(renderChatItem)}
              </Grid>
            </Grid>
          </Grid>
        )}
        {(isSmallScreen ? location.pathname === chatPath : true) && (
          <Grid
            item
            xs={12}
            md={8}
            className={cls.messageSection}
            style={{ height: "100%", position: "relative", overflow: "hidden", minWidth: 0 }}
          >
            {selectedThread && (
              <ChatProvider
                selectedUser={selectedThread}
                fetchUserList={() => {}}
              >
                <Outlet />
              </ChatProvider>
            )}
            {!selectedThread && (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <NCText style={{ color: "#9CA3AF", fontSize: 16 }}>
                  Select a conversation to start messaging
                </NCText>
              </Box>
            )}
          </Grid>
        )}
        </Grid>
      </Box>
      {isLoading && <NCLoader open={true} />}

      {/* New Chat Modal */}
      <NCModal
        open={newGroupModal}
        title="Create Chat"
        text={groupModalContent}
        onClose={() => { setNewGroupModal(false); chatFormik.resetForm() }}
        submitButton={
          <NCButton
            variant="contained"
            color="primary"
            disableElevation
            disabled={!chatFormik.values.group_name?.trim() || chatFormik.isSubmitting}
            onClick={chatFormik.handleSubmit}
            style={{ width: "auto" }}
          >
            Create Chat
          </NCButton>
        }
      />
    </Grid>
  )
}

export default UserChannelList
